import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { GameView } from '@/shared/game/types'
import { postMessage } from './api'
import { allowsFreeText, showsHistory } from './rules'
import { toggleMuted, useMutedSeats } from './settings'
import type { ChatMessage } from './types'
import { chatKey } from './useChat'
import { say, usePhrases } from './usePhrases'

/** Предел свободного текста — тот же, что у сервера (GameMessage::MAX_LENGTH). */
const MAX_LENGTH = 200

type Props = {
  view: GameView
  messages: ChatMessage[]
}

/**
 * Разговор и способ в него вступить — без всякой рамки вокруг.
 *
 * Одно и то же тело и в лобби (там чат — обычная карточка, места хватает), и
 * в шторке поверх доски: разговор один, а поводов написать его дважды нет.
 * Про DOM здесь ничего не знается, кроме прокрутки списка вниз, — мобильному
 * приложению нужна общая логика при двух разных визуалах (docs/MOBILE.md).
 */
export function ChatBox({ view, messages }: Props) {
  const client = useQueryClient()
  const phrases = usePhrases()
  const muted = useMutedSeats(view.id)
  const [text, setText] = useState('')
  const [showPhrases, setShowPhrases] = useState(true)
  const tail = useRef<HTMLDivElement>(null)

  // Оба правила — про то, кто за столом сидит, и оба разобраны в rules.ts.
  const freeText = allowsFreeText(view)
  const history = showsHistory(view)
  const seated = view.you !== null

  const send = useMutation({
    mutationFn: (message: { kind: 'phrase' | 'text'; body: string }) =>
      postMessage(view.id, message.kind, message.body),
    onSuccess: (message) => {
      /*
       * Своя реплика дописывается ответом, а не событием: событие о своём
       * действии сервер нам не присылает (X-Socket-ID), потому что свежее
       * уже вернулось этим самым запросом.
       */
      client.setQueryData<ChatMessage[]>(chatKey(view.id), (old) =>
        [...(old ?? []), message].sort((a, b) => a.seq - b.seq),
      )
      setText('')
    },
  })

  // Список читается снизу: свежее внизу, как в любом разговоре.
  const count = messages.length

  useEffect(() => {
    const box = tail.current

    if (box === null || count === 0) return

    // scrollTop, а не scrollTo: последнего в jsdom нет вовсе, а плавность
    // здесь и не нужна — список должен просто стоять на свежем.
    box.scrollTop = box.scrollHeight
  }, [count])

  const nameOf = (seat: number) => view.players.find((player) => player.seat === seat)
  const others = view.players.filter((player) => player.seat !== view.you?.seat)

  return (
    <div className="chat">
      {/*
        История — только за закрытым столом (см. rules.ts). За открытым
        сказанное видно плашкой у двора говорившего и живёт несколько секунд:
        список чужих «ага» между незнакомцами не нужен никому из них.
      */}
      {history && (
        <div className="chat__log" ref={tail}>
          {messages.length === 0 ? (
            <p className="muted">Пока тихо. За столом можно поздороваться.</p>
          ) : (
            messages
              .filter((message) => !muted.includes(message.seat))
              .map((message) => {
                const player = nameOf(message.seat)
                const own = message.seat === view.you?.seat

                return (
                  <p className={`chat__line${own ? ' chat__line--own' : ''}`} key={message.seq}>
                    {/* Цвет — по МЕСТУ из view.players, а не из реплики: до
                        старта партии цвета не существует вовсе, а после он
                        известен по столу. */}
                    <span
                      className={`player__dot${
                        player?.color === null || player?.color === undefined
                          ? ''
                          : ` dot-${player.color}`
                      }`}
                    />
                    <b className="chat__who">{message.author}</b>
                    <span className="chat__text">
                      {say(phrases.data, message.kind, message.body)}
                    </span>
                  </p>
                )
              })
          )}
        </div>
      )}

      {seated && (
        <>
          {/*
            Набор — одним блоком из двух рядов: эмодзи отдельно от слов (они
            короткие и в общем потоке теряются между фразами), но прокрутка,
            если она вообще понадобится, у них общая. Раньше ряд эмодзи ездил
            по горизонтали сам, и половина смайлов просто не была видна.

            Оба ряда ПЕРЕНОСЯТСЯ: чипов двадцать два, и ни один не должен
            уезжать за край — до кнопки, которую не видно, никто не долистает.
          */}
          <div className="chat__picker">
            <div className="chat__emoji">
              {(phrases.data ?? [])
                .filter((phrase) => phrase.emoji)
                .map((phrase) => (
                  <button
                    className="chat__pick chat__pick--emoji"
                    key={phrase.code}
                    type="button"
                    disabled={send.isPending}
                    onClick={() => send.mutate({ kind: 'phrase', body: phrase.code })}
                  >
                    {phrase.text}
                  </button>
                ))}
            </div>

            {showPhrases && (
              <div className="chat__phrases">
                {(phrases.data ?? [])
                  .filter((phrase) => !phrase.emoji)
                  .map((phrase) => (
                    <button
                      className="chat__pick"
                      key={phrase.code}
                      type="button"
                      disabled={send.isPending}
                      onClick={() => send.mutate({ kind: 'phrase', body: phrase.code })}
                    >
                      {phrase.text}
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Свободный текст — только за закрытым столом. За открытым поля
              ввода просто нет, и объяснять это словами не нужно: набор
              готовых фраз говорит сам за себя. */}
          {freeText && (
            <form
              className="chat__field"
              onSubmit={(event) => {
                event.preventDefault()

                if (text.trim() !== '') send.mutate({ kind: 'text', body: text })
              }}
            >
              <input
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Написать за столом"
                aria-label="написать за столом"
                maxLength={MAX_LENGTH}
                /* Фразы уступают место клавиатуре: на телефоне она лезет
                   поверх доски, и два ряда кнопок над ней уже некуда. */
                onFocus={() => setShowPhrases(false)}
                onBlur={() => setShowPhrases(true)}
              />
              {/* Своим классом, а не общим .primary: у того нижняя грань и
                  подскок на наведении — по кнопке игры стучат, как по фишке,
                  а кнопка чата стоит в прокручиваемой шторке, и подскок
                  подрезался её краем. */}
              <button className="chat__send" type="submit" disabled={send.isPending}>
                Сказать
              </button>
            </form>
          )}

          {send.isError && <p className="muted">реплика не дошла — попробуйте ещё раз</p>}

          {/* Заткнуть соперника — здесь же, а не в настройках: приходят
              за этим ровно в тот момент, когда он надоел. Только в браузере:
              серверных жалоб в игре нет. */}
          {others.length > 0 && (
            <div className="chat__mutes">
              {/* Словами, а не значком: 🔊 и 🔇 отличаются одной чёрточкой,
                  а мелким кеглем разными шрифтами рисуются по-разному —
                  на телефоне их было не различить вовсе. */}
              <span className="chat__mutes-label">Не слышать:</span>
              {others.map((player) => (
                <button
                  className={`chat__mute${muted.includes(player.seat) ? ' chat__mute--off' : ''}`}
                  key={player.seat}
                  type="button"
                  aria-pressed={muted.includes(player.seat)}
                  onClick={() => toggleMuted(view.id, player.seat)}
                >
                  {player.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
